import { captureWarn } from './errorUtils';
import { registerTestApi } from './testUtils';

export const ORCHESTRATOR_STORAGE_KEY = 'orchestrator-data';
export const ORCHESTRATOR_STORAGE_EVENT = 'orchestrator-storage-change';

export interface WaitListEntry {
  id: string;
  weight: number;
  timestamp: number;
}

export interface OrchestratorData {
  canRenderNudge: boolean;
  activeComponentId: string | null;
  waitList: WaitListEntry[];
}

export type OrchestratorStorageListener = (data: OrchestratorData) => void;

const DEFAULT_ORCHESTRATOR_DATA: OrchestratorData = {
  canRenderNudge: false,
  activeComponentId: null, // by default disable rendering
  waitList: [],
};

/**
 *OrchestratorStorage manages orchestrator data in sessionStorage with real-time synchronization
 *  within the same tab using a combination of native storage events
 * and custom events.
 *
 * This class provides a centralized way to store and retrieve orchestrator configuration data,
 * automatically notifying subscribers when the data changes from any source.
 *
 * Key Features:
 * - Same-tab synchronization using custom 'orchestrator-storage-change' events
 * - Subscription-based listener pattern for real-time updates
 * - Graceful error handling for JSON parsing and storage operations
 * - Singleton pattern ensuring consistent state across the application
 *
 * Note: This class is only exported because we need to instanciate it in a test, you should not
 * create multiple instances of this class in your application and you should always use the
 * `orchestratorStorage` singleton instance.
 */
export class OrchestratorStorage {
  // Set of listeners to notify when orchestrator data changes
  private listeners: Set<OrchestratorStorageListener> = new Set();

  // Flag to ensure setup is only done once
  private setupCompleted = false;

  // Cache the last parsed data and the raw string to avoid unnecessary parsing
  private lastRawData: string | null = null;
  private cachedData: OrchestratorData | null = null;

  constructor() {
    this.setup();
  }

  /**
   * Initialize event listeners for storage changes and set default data
   */
  private setup(): void {
    // Only initialize once across all windows/iframes
    if (this.setupCompleted) {
      return;
    }

    this.setupCompleted = true;

    // Check if this window is the top-level window (not inside an iframe)
    // If true, initialize the orchestrator with default data
    if (window.top === window) {
      this.updateData(DEFAULT_ORCHESTRATOR_DATA);

      // Listen for storage changes from other tabs/windows
      // Use the top window to avoid duplicate listeners
      window.addEventListener('storage', this.handleStorageEvent);

      // Listen for custom storage changes within the same tab
      window.addEventListener(
        ORCHESTRATOR_STORAGE_EVENT,
        this.handleCustomStorageEvent as EventListener
      );
    }
  }

  /**
   * Handle storage events from other tabs/windows
   * This allows us to update the orchestrator data when it changes in another tab
   */
  private handleStorageEvent = (event: StorageEvent) => {
    if (event.key === ORCHESTRATOR_STORAGE_KEY && event.newValue) {
      try {
        // Invalidate cache since data changed externally
        this.cachedData = null;
        this.lastRawData = null;

        const newData = JSON.parse(event.newValue);
        this.notifyListeners(newData);
      } catch (error) {
        captureWarn(
          'Failed to parse orchestrator data from storage event',
          error
        );
      }
    }
  };

  /**
   * Handle custom storage events for same-tab communication
   * This allows us to update the orchestrator data without relying on the storage event
   */
  private handleCustomStorageEvent = (event: CustomEvent) => {
    if (event.detail.key === ORCHESTRATOR_STORAGE_KEY) {
      try {
        // Invalidate cache since data changed externally
        this.cachedData = null;
        this.lastRawData = null;

        const newData = JSON.parse(event.detail.newValue);
        this.notifyListeners(newData);
      } catch (error) {
        captureWarn(
          'Failed to parse orchestrator data from custom storage event',
          error
        );
      }
    }
  };

  /**
   * Notify all listeners with the new orchestrator data
   */
  private notifyListeners(data: OrchestratorData) {
    this.listeners.forEach((listener) => listener(data));
  }

  /**
   * Get the storage instance
   */
  private getStorage(): Storage {
    return sessionStorage;
  }

  /**
   * Get the current orchestrator data from sessionStorage
   * Uses caching to avoid unnecessary JSON parsing and object creation
   */
  getData(): OrchestratorData {
    if (!this.setupCompleted) {
      return DEFAULT_ORCHESTRATOR_DATA;
    }

    try {
      const storedData = this.getStorage().getItem(ORCHESTRATOR_STORAGE_KEY);

      // If no data in storage, return default
      if (!storedData) {
        // Cache the default data reference
        if (!this.cachedData || this.lastRawData !== null) {
          this.cachedData = DEFAULT_ORCHESTRATOR_DATA;
          this.lastRawData = null;
        }
        return this.cachedData;
      }

      // If the raw data hasn't changed, return the cached parsed data
      if (this.lastRawData === storedData && this.cachedData) {
        return this.cachedData;
      }

      // Parse and cache the new data
      const parsedData = JSON.parse(storedData);
      this.cachedData = parsedData;
      this.lastRawData = storedData;

      return parsedData;
    } catch {
      // On error, cache and return default data
      if (!this.cachedData || this.lastRawData !== null) {
        this.cachedData = DEFAULT_ORCHESTRATOR_DATA;
        this.lastRawData = null;
      }
      return this.cachedData;
    }
  }

  /**
   * Set the active component ID
   * Add component to the wait list
   */
  setActiveComponent(componentId: string, weight = 1): void {
    const currentData = this.getData();

    // if there's an active component, skip everything
    if (!componentId || currentData.activeComponentId !== null) {
      return;
    }

    const existingEntry = currentData.waitList.find(
      (entry) => entry.id === componentId
    );

    // If already in wait list, don't add again
    if (existingEntry) {
      return;
    }

    // Add to wait list
    const newWaitList = [
      ...currentData.waitList,
      {
        id: componentId,
        weight,
        timestamp: Date.now(),
      },
    ];

    this.updateData({
      waitList: newWaitList,
    });
  }

  removeActiveComponent(componentId: string): void {
    if (!componentId) {
      return;
    }

    const currentData = this.getData();

    // Remove from wait list if present
    const newWaitList = currentData.waitList.filter(
      (entry) => entry.id !== componentId
    );

    // If this is the active component, clear it
    if (currentData.activeComponentId === componentId) {
      this.updateData({
        // free the spot for another component to claim
        activeComponentId: null,

        // This will prevent rendering until the Orchestrator allow it again
        canRenderNudge: false,

        // Update wait list
        waitList: newWaitList,
      });
    } else if (newWaitList.length !== currentData.waitList.length) {
      // Only in wait list, not active - just remove from wait list
      this.updateData({
        waitList: newWaitList,
      });
    }
  }

  /**
   * Allow rendering of unmanaged nudges
   * Process the wait list, select the highest weight component, and set it as active
   */
  allowRender() {
    const currentData = this.getData();

    // If wait list is empty, do nothing
    if (currentData.waitList.length === 0) {
      return;
    }

    // Sort wait list by weight (descending), then by timestamp (ascending for FIFO)
    const sortedWaitList = [...currentData.waitList].sort((a, b) => {
      // Higher weight wins
      if (a.weight !== b.weight) {
        return b.weight - a.weight;
      }
      // For same weight, earlier timestamp wins (FIFO)
      return a.timestamp - b.timestamp;
    });

    // Get the winner
    const winner = sortedWaitList[0];

    // Set the winner as active and clear the wait list
    this.updateData({
      canRenderNudge: true,
      activeComponentId: winner.id,
      waitList: [],
    });
  }

  /**
   * Update the orchestrator data in sessionStorage
   */
  updateData(newData: Partial<OrchestratorData>): OrchestratorData {
    const currentData = this.getData();
    const updatedData = { ...currentData, ...newData };

    try {
      const serializedData = JSON.stringify(updatedData);

      // Save to sessionStorage
      this.getStorage().setItem(ORCHESTRATOR_STORAGE_KEY, serializedData);

      // Update cache with the new data
      this.cachedData = updatedData;
      this.lastRawData = serializedData;

      // Dispatch custom event for same-tab communication
      window.dispatchEvent(
        new CustomEvent(ORCHESTRATOR_STORAGE_EVENT, {
          detail: {
            key: ORCHESTRATOR_STORAGE_KEY,
            newValue: serializedData,
            oldValue: JSON.stringify(currentData),
          },
        })
      );
    } catch (error) {
      captureWarn('Failed to save orchestrator data to sessionStorage', error);
    }

    return updatedData;
  }

  /**
   * Clear all orchestrator data from sessionStorage
   */
  clearData(): void {
    try {
      // Dispatch custom event to notify about clearing
      window.dispatchEvent(
        new CustomEvent(ORCHESTRATOR_STORAGE_EVENT, {
          detail: {
            key: ORCHESTRATOR_STORAGE_KEY,
            newValue: JSON.stringify(DEFAULT_ORCHESTRATOR_DATA),
            oldValue: JSON.stringify(this.getData()),
          },
        })
      );

      // Clear sessionStorage
      this.getStorage().removeItem(ORCHESTRATOR_STORAGE_KEY);

      // Reset cache to default data
      this.cachedData = DEFAULT_ORCHESTRATOR_DATA;
      this.lastRawData = null;
    } catch (error) {
      captureWarn(
        'Failed to clear orchestrator data from sessionStorage',
        error
      );
    }
  }

  /**
   * Subscribe to data changes
   */
  subscribe(listener: OrchestratorStorageListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    this.clearData();

    const topWindow = window.top || window;
    topWindow.removeEventListener('storage', this.handleStorageEvent);
    topWindow.removeEventListener(
      ORCHESTRATOR_STORAGE_EVENT,
      this.handleCustomStorageEvent as EventListener
    );
    this.listeners.clear();
    this.setupCompleted = false;
  }
}

// Use a unique symbol to avoid naming conflicts
const STORAGE_KEY = Symbol.for('orchestrator-storage-singleton');

interface OrchestratorStorageWindow extends Window {
  [STORAGE_KEY]?: OrchestratorStorage;
}

// Ensure we always use the same instance across all windows/iframes
// by storing it on the top-level window
const getOrCreateStorage = (): OrchestratorStorage => {
  try {
    const topWindow = (window.top || window) as OrchestratorStorageWindow;

    // Check if the singleton already exists on the top window
    if (!topWindow[STORAGE_KEY]) {
      topWindow[STORAGE_KEY] = new OrchestratorStorage();
    }

    return topWindow[STORAGE_KEY];
  } catch (error) {
    captureWarn('Failed to get or create orchestrator storage', error);
    return new OrchestratorStorage();
  }
};

// Create a singleton instance shared across all windows/iframes
export const orchestratorStorage = getOrCreateStorage();

// Register test API (only registers when in Whisper bundle + Kitchen Sink)
registerTestApi({ orchestratorStorage });
